import React, { useState, useEffect } from 'react';
import './CurrentPlayback.css';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:5000';

const CurrentPlayback = ({ refreshTick = 0 }) => {
  const [playbackState, setPlaybackState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shuffling, setShuffling] = useState(false);
  // New: user choice for how many tracks to queue next
  const [numToQueue, setNumToQueue] = useState(10);

  useEffect(() => {
    fetchPlaybackState();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick]);

  useEffect(() => {
    // Initial load + periodic refresh
    fetchPlaybackState();
    const interval = setInterval(fetchPlaybackState, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchPlaybackState = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/playback-state`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch playback state');
      }
      const data = await response.json();
      setPlaybackState(data);
      setError(null);
    } catch (err) {
      setError('Failed to load current playback state');
    } finally {
      setLoading(false);
    }
  };

  const handleShuffleCurrent = async () => {
    if (!playbackState?.context?.id) return;

    setShuffling(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/shuffle-playlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ 
          playlistId: playbackState.context.id,
          numRequestedShuffledTracks: Number(numToQueue) || 10
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Refresh playback state after shuffling
        setTimeout(fetchPlaybackState, 2000);
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError(`Error: ${error.message}`);
    } finally {
      setShuffling(false);
    }
  };

  const handleNumChange = (e) => {
    const v = parseInt(e.target.value, 10);
    if (Number.isNaN(v)) {
      setNumToQueue(10);
      return;
    }
    setNumToQueue(v);
  };

  if (loading) {
    return (
      <div className="current-playback">
        <div className="loading">
          <div className="spinner"></div>
          <p>Checking your current playback...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="current-playback">
        <div className="error-state">
          <p>❌ {error}</p>
        </div>
      </div>
    );
  }

  if (!playbackState?.isPlaying) {
    return (
      <div className="current-playback">
        <div className="not-playing-state">
          <h2>🎵 Nothing Currently Playing</h2>
          <p>Open Spotify and start playing any song from the playlist you want to shuffle, then return here and tap Refresh.</p>
        </div>
      </div>
    );
  }

  if (playbackState.context?.type !== 'playlist') {
    return (
      <div className="current-playback">
        <div className="not-playlist-state">
          <h2>🎧 Playing from {playbackState.context?.type || 'Unknown Source'}</h2>
          <p>Start playing a song from a playlist (liked songs don't count!) in Spotify, then return here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="current-playback">
      <div className="now-playing">
        <h2>🎵 Now Playing</h2>
        
        {playbackState.track && (
          <div className="track-info">
            {playbackState.track.album?.images?.[0] && (
              <img 
                src={playbackState.track.album.images[0].url} 
                alt={playbackState.track.album.name}
                className="album-art"
              />
            )}
            <div className="track-details">
              <h3 className="track-name">{playbackState.track.name}</h3>
              <p className="artist-name">
                {playbackState.track.artists?.map(artist => artist.name).join(', ')}
              </p>
              <p className="album-name">{playbackState.track.album?.name}</p>
            </div>
          </div>
        )}

        {playbackState.context && (
          <div className="context-info">
            <h4>📋 From Playlist</h4>
            <p className="playlist-name">{playbackState.context.name}</p>
          </div>
        )}

        {playbackState.device && (
          <div className="device-info">
            <h4>🔊 Playing on</h4>
            <p className="device-name">{playbackState.device.name} ({playbackState.device.type})</p>
          </div>
        )}

        <div className="actions">
          <div className="queue-controls">
            <label htmlFor="numToQueue" className="queue-label">Add</label>
            <div className="queue-select-wrapper">
              <select
                id="numToQueue"
                value={numToQueue}
                onChange={handleNumChange}
                className="num-select"
                aria-label="Number of tracks to add next"
              >
                {[5, 10, 15, 20, 25, 30, 40, 50, 80].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <span className="queue-units">TRULY shuffled tracks to the queue</span>
          </div>

          <button 
            onClick={handleShuffleCurrent}
            disabled={shuffling}
            className={`btn shuffle-btn ${shuffling ? 'disabled' : ''}`}
          >
            {shuffling ? (
              <>
                <div className="btn-spinner"></div>
                🔄 Shuffling...
              </>
            ) : (
              <>
                🎲 Shuffle This Playlist
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CurrentPlayback;
